import { useLocation } from "@reach/router";
import { Link } from "gatsby";
import * as React from "react";
import { useElementSize } from "../../hooks/useElementSize";
import { useLoc } from "../../hooks/useLoc";
import { useMonth } from "../../hooks/useMonth";
import { monthSlugsWithOverview as monthSlugs } from "../../services/centralStore";
import ensureMonthIndexInRange from "../../services/ensureMonthIndexInRange";

export const DestinationMonthPicker = ({
    omitLinks = false
}: {
    omitLinks?: boolean;
}) => {
    let { month: activeMonth, setMonth: setActiveMonth } = useMonth({});
    let [monthsToRender, setMonthsToRender] = React.useState([...monthSlugs]);
    let alternativeCenterMonth: string = null;
    const { pathname } = useLocation();
    const loc = useLoc();
    const handleClick = (month: string) => {
        setActiveMonth(month);
    };
    let width = useElementSize({});
    const renderWithoutWidth = !!!width;
    width = width || 1600;
    let breakingIndex = 0;
    if (width > 1560) {
        if (monthsToRender.length <= 12) {
            setMonthsToRender([...monthSlugs]);
            return null;
        }
    } else {
        let activeMonthIndex = monthSlugs.indexOf(activeMonth);
        if (activeMonth == "climate") {
            activeMonthIndex = ensureMonthIndexInRange(
                new Date().getMonth() + 1
            );
            alternativeCenterMonth = monthSlugs[activeMonthIndex];
        } else {
            alternativeCenterMonth = "climate";
        }
        let idxs = [-2, -1, 1, 2].map((diff) =>
            ensureMonthIndexInRange(activeMonthIndex + diff)
        );
        let months;
        if (width < 780) {
            months = [
                monthSlugs[idxs[1]],
                alternativeCenterMonth,
                monthSlugs[idxs[2]]
            ];
            breakingIndex = 1;
        } else {
            months = [
                monthSlugs[idxs[0]],
                monthSlugs[idxs[1]],
                alternativeCenterMonth,
                monthSlugs[idxs[2]],
                monthSlugs[idxs[3]]
            ];
            breakingIndex = 2;
        }
        if (
            monthsToRender.length != months.length ||
            monthsToRender[0] != months[0] ||
            monthsToRender[breakingIndex] != months[breakingIndex]
        ) {
            setMonthsToRender(months);
            return null;
        }
    }

    return (
        <>
            {alternativeCenterMonth && (
                <div
                    className={`absolute bottom-0 flex w-full justify-center text-[14px] font-bold tracking-[0.39px] text-white shadow-sm-down`}
                >
                    <div
                        className={`px-monthlink-div rounded-t-[4px] border-none bg-white py-[12px] text-zinc-800`}
                    >
                        {loc("common.months." + activeMonth)}
                    </div>
                </div>
            )}
            <div
                className={`absolute bottom-0 m-auto flex w-full justify-center text-[14px] font-bold tracking-[0.39px] text-white shadow-sm-down
                ${alternativeCenterMonth && width >= 780 && "max-w-[780px]"}
                ${alternativeCenterMonth && width < 780 && "max-w-[520px]"}
                ${alternativeCenterMonth && "right-0 left-0"}
                ${renderWithoutWidth && "opacity-5"}`}
            >
                {monthsToRender.map((month, idx) =>
                    omitLinks ? (
                        <PickerDiv
                            key={idx}
                            {...{
                                month,
                                activeMonth,
                                alternativeCenterMonth,
                                idx,
                                breakingIndex,
                                handleClick
                            }}
                        />
                    ) : (
                        <PickerLink
                            key={idx}
                            {...{
                                month,
                                activeMonth,
                                pathname,
                                alternativeCenterMonth,
                                idx,
                                breakingIndex,
                                handleClick
                            }}
                        />
                    )
                )}
            </div>{" "}
        </>
    );
};

const PickerDiv = ({
    month,
    alternativeCenterMonth,
    activeMonth,
    handleClick,
    breakingIndex,
    idx
}: InnerLinkProps) => {
    const loc = useLoc();
    return (
        <div
            key={month}
            className={`px-monthlink-div ${
                alternativeCenterMonth ? "py-[12px]" : "py-[24px]"
            } cursor-pointer rounded-t-[4px] border-none
                ${generateClasses({
                    alternativeCenterMonth,
                    idx,
                    breakingIndex,
                    month,
                    activeMonth
                })}`}
            onClick={() => handleClick(month)}
        >
            {(breakingIndex && idx < breakingIndex
                ? getArrowsFromIndex(idx - breakingIndex)
                : "") +
                loc("common.months." + month) +
                (breakingIndex && idx > breakingIndex
                    ? getArrowsFromIndex(idx - breakingIndex)
                    : "")}
        </div>
    );
};

const PickerLink = ({
    month,
    activeMonth,
    pathname,
    alternativeCenterMonth,
    idx,
    breakingIndex,
    handleClick
}: InnerLinkProps) => {
    const loc = useLoc();
    return (
        <Link
            key={month}
            className={`px-monthlink-div ${
                alternativeCenterMonth ? "py-[12px]" : "py-[24px]"
            } rounded-t-[4px] border-none
                ${generateClasses({
                    alternativeCenterMonth,
                    idx,
                    breakingIndex,
                    month,
                    activeMonth
                })}`}
            to={pathname.split("-").slice(0, -1).join("-") + `-` + month + "/"}
            //onClick={() => handleClick(month)}
        >
            {(breakingIndex && idx < breakingIndex
                ? getArrowsFromIndex(idx - breakingIndex)
                : "") +
                loc("common.months." + month) +
                (breakingIndex && idx > breakingIndex
                    ? getArrowsFromIndex(idx - breakingIndex)
                    : "")}
        </Link>
    );
};
const generateClasses = ({
    alternativeCenterMonth,
    idx,
    breakingIndex,
    month,
    activeMonth
}) => {
    return `${alternativeCenterMonth && "flex-1"}
                ${alternativeCenterMonth && idx > breakingIndex && "text-left"}
                ${alternativeCenterMonth && idx < breakingIndex && "text-right"}
                ${
                    alternativeCenterMonth &&
                    idx == breakingIndex &&
                    "text-center relative bottom-[40px]"
                }
                ${month == activeMonth && "bg-white text-zinc-800"}`;
};

type InnerLinkProps = {
    month: string;
    activeMonth: string;
    pathname?: string;
    alternativeCenterMonth: string;
    idx: number;
    breakingIndex: number;
    handleClick: (month: string) => void;
};

const getArrowsFromIndex = (idx: number) => {
    if (!idx) return "";
    switch (idx) {
        case -2:
            return "<< ";
        case -1:
            return "< ";
        case 1:
            return " >";
        case 2:
            return " >>";
    }
};
